<template>
  <div
    :data-cy-status-bar-title="title"
    :class="`status-bar-license-info status-bar-license-info-${size}`"
  >
    <q-resize-observer @resize="handleResize" />

    <div class="row justify-between items-center status-bar-license-title-container">
      <div>
        <div
          v-if="showTitle"
          class="sbli--title gordita-500-14-m"
          data-testid="status-bar-license-info-helper-cores-title"
        >
          <div class="flex items-center no-wrap">
            <div
              v-if="showSCLSection"
              class="mr-[2px]"
            >
              <i class="incredicon-solid-windows !text-[16px] text-Text/text_medium" />
              <i class="uil uil-linux !text-[16px] text-Text/text_medium" />
            </div>
            <div>{{ title }}</div>
          </div>
        </div>

        <slot
          name="header-postpend"
          :size="size"
        />
        <div
          class="sbli--subtitle sans-700-26 text-no-wrap"
          :data-testid="testId"
        >
          {{ String(totalInfo).length >= 6 || compactNumFormat ? millify(totalInfo, { precision: 1 }) : totalInfo?.toLocaleString() }}
        </div>
      </div>
    </div>

    <div class="column justify-end legend-rows gap-[2px]">
      <legend-key-value
        v-if="info.assigned !== undefined"
        :test-id="'assigned-' + testId"
        :color="COLORS['System/app_secondary']"
        :text="hasBuildCacheLicense || isCloudData ? $t('dashboard.top.topRightBars.legend.used') : $t('dashboard.top.topRightBars.legend.assigned')"
        :value="info.assigned"
      />
      <legend-key-value
        v-if="info.cooldown > 0"
        :test-id="'cooldown-' + testId"
        :color="COLORS['Text/text_medium']"
        :text="$t('dashboard.top.topRightBars.legend.cooldown')"
        :value="info.cooldown"
        :tooltip-info="{ displayTooltip: true, cooldownNextRelease: info.cooldownNextRelease }"
      />
      <legend-key-value
        v-if="info.available !== undefined"
        :test-id="'available-' + testId"
        :color="COLORS['System/app_grey_89']"
        :text="$t('dashboard.top.topRightBars.legend.available')"
        :value="info.available"
      />
      <legend-key-value
        v-if="hasBuildCacheLicense"
        :test-id="'reset-' + testId"
        :color="COLORS['System/app_grey_89']"
        :text="$t('dashboard.top.topRightBars.legend.reset')"
        :value="nextResetIn"
        :hide-legend-prop="hasBuildCacheLicense"
      />
    </div>
    <div class="total-info-big sans-700-16">{{ String(totalInfo).length >= 6 ? millify(totalInfo) : totalInfo?.toLocaleString() }}</div>
    <div
      class="progress"
      :style="progressStyle"
    />
    <!-- <div class="status-bar-license-info--tooltip"> -->
    <q-tooltip
      v-if="size === 'small'"
      class="tooltip-background flex column"
      style="padding: 8px 12px 8px 12px; border-radius: 2px; gap: 8px"
      :delay="300"
    >
      <slot name="tooltip">
        <legend-key-value
          v-if="info.assigned !== undefined"
          test-id="status-bar-info-assigned"
          :bold="true"
          text-color="white"
          :color="COLORS['System/app_secondary']"
          :text="hasBuildCacheLicense || isCloudData ? $t('dashboard.top.topRightBars.legend.used') : $t('dashboard.top.topRightBars.legend.assigned')"
          :value="info.assigned"
        />
        <legend-key-value
          v-if="info.cooldown > 0"
          test-id="status-bar-info-cooldown"
          :bold="true"
          text-color="white"
          :color="COLORS['Text/text_medium']"
          :text="$t('dashboard.top.topRightBars.legend.cooldown')"
          :value="info.cooldown"
        />
        <legend-key-value
          v-if="info.available !== undefined"
          test-id="status-bar-info-available"
          :bold="true"
          text-color="white"
          :color="COLORS['System/app_grey_89']"
          :text="$t('dashboard.top.topRightBars.legend.available')"
          :value="info.available"
        />
        <legend-key-value
          v-if="hasBuildCacheLicense"
          test-id="status-bar-info-reset"
          :color="COLORS['System/app_grey_89']"
          :text="$t('dashboard.top.topRightBars.legend.reset')"
          :value="nextResetIn"
          :hide-legend-prop="hasBuildCacheLicense"
        />
      </slot>
    </q-tooltip>

    <!-- </div> -->
  </div>
</template>

<script>
import millify from 'millify';
import { inject } from 'vue';
import { useAgentsPageQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import COLORS from '@/design-system/colors.json';
import { Constants } from '@/const/globals';
import { LICENSE_INFO_TITLE } from '@/components/status-bar/components/CONST';
import LegendKeyValue from './legend-key-value.vue';

export default {
  components: { LegendKeyValue },

  props: {
    testId: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: String,
    },
    info: {
      type: Object,
      default: () => ({
        assigned: 4,
        available: 3,
        cooldown: 1,
      }),
      validator: val => {
        const types = ['assigned', 'available', 'cooldown'];
        return typeof val === 'object' && Object.keys(val).every(key => types.includes(key) && typeof val[key] === 'number');
      },
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    numOfBlocks: {
      type: Number,
      default: 1,
    },
    hasBuildCacheLicense: {
      type: Boolean,
      default: false,
    },
    nextResetIn: {
      type: String,
      default: '',
    },
    isCloudData: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const coordinatorsService = inject(IKCoordinatorsService);

    const GQL_AgentsPageQuery = useAgentsPageQuery(() => ({
      coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
    }));
    return { GQL_AgentsPageQuery, COLORS };
  },
  data() {
    return {
      elSize: { width: null, height: null },
    };
  },

  computed: {
    millify() {
      return millify;
    },
    showSCLSection() {
      return (
        this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.license?.windows?.features?.productFeatures?.includes(Constants.LICENSE_FEATURES.SCL) &&
        LICENSE_INFO_TITLE.helperCores === this.title &&
        this.size !== 'small'
      );
    },
    totalInfo() {
      return (this.info.assigned || 0) + (this.info.available || 0) + (this.info.cooldown || 0);
    },
    progressStyle() {
      const gradientPrecentage = Object.keys(this.info).reduce((acc, key) => ({ ...acc, [key]: (this.info[key] / (this.totalInfo || 1)) * 100 }), {});
      const { assigned = 0 } = gradientPrecentage;
      const cooldown = (gradientPrecentage.assigned || 0) + (gradientPrecentage.cooldown || 0);
      const available = (gradientPrecentage.assigned || 0) + (gradientPrecentage.cooldown || 0) + (gradientPrecentage.available || 0);
      return {
        background: `linear-gradient(to right, ${this.COLORS['System/app_secondary']} ${assigned}%, ${this.COLORS['Text/text_medium']} ${assigned}% ,${this.COLORS['Text/text_medium']} ${cooldown}%, ${this.COLORS['System/app_grey_89']} ${cooldown}%, ${this.COLORS['System/app_grey_89']} ${available}%)`,
      };
    },
    compactNumFormat() {
      // Applying compact number format for 5 block in medium size
      return this.numOfBlocks === 5 && this.size === 'medium';
    },
  },
  methods: {
    handleResize(value) {
      this.elSize = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.status-bar-license-info.status-bar-license-info-medium {
  .progress {
    margin-top: 6px;
  }

  .legend-rows {
    display: flex;
    margin-top: auto;
  }
  .total-info-big {
    display: none;
  }
  .status-bar-license-title-container {
    white-space: nowrap;
  }
  .sbli--title {
    font-size: 13px !important;
    line-height: 20px;
    height: 20px;
    text-align: left;
  }
  .sbli--subtitle {
    font-size: 20px !important;
    line-height: 27px;
    height: 27px;
    display: block;
  }
  .status-bar-license-info {
    .progress {
      margin-top: unset;
    }
  }
}
.status-bar-license-info.status-bar-license-info-large {
  padding: 0 16px 0 16px;
  .progress {
    margin-top: 6px;
  }

  .legend-rows {
    display: flex;
    margin-top: auto;
  }
  .total-info-big {
    display: none;
  }
  .status-bar-license-title-container {
    white-space: nowrap;
  }
  .sbli--title {
    font-size: 14px;
    text-align: left;
  }
  .sbli--subtitle {
    display: block;
  }
  .status-bar-license-info {
    .progress {
      margin-top: unset;
    }
  }
}

.status-bar-license-info-small {
  padding: 0 8px 0 8px !important;
  flex: 1;
  .progress {
    margin-top: auto;
  }
  .status-bar-license-title-container {
    margin: 0 auto;
  }
  .sbli--title {
    font-size: 12px;
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

.status-bar-license-info-small {
  .sbli--title {
    height: 32px;
  }
  .total-info-big {
    font-size: 16px;
    margin: auto;
    height: 40px;
  }
  pointer-events: initial;
  cursor: pointer;
}
.legend-rows {
  display: none;
}
.total-info-big {
  display: flex;
}

.sbli--title {
  width: min-content;
  line-height: 16px;
  height: 20px;
  text-align: center;
}
.sbli--subtitle {
  display: none;
}
.status-bar-license-info {
  color: $-text--dark;
  display: inline-flex;
  flex-direction: column;
  padding: 0 12px 0px 12px;
}
.status-bar-license-title-container {
  gap: 10px;
  margin-bottom: 9px;
  flex-wrap: nowrap;
  display: flex;
  align-items: flex-start;
}
.progress {
  border-radius: 50px;
  height: 8px;
  width: 100%;
}
.legend-rows {
  flex-wrap: nowrap;
  :deep(.legend-key-value) {
    height: 16px;
  }
}
.total-info-big {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
